import { call, put, takeEvery } from "redux-saga/effects";
import { createAction } from "@reduxjs/toolkit";

import { reloadPosts as reloadPostsAction } from "actions/postsView";
import { closeModal, openModal, registerModal } from "libs/modals";

import { Post } from "../types";
import { editPost } from "./model";
import { SaveFormResult } from "../../../components/Posts/PostForm/types";

const EDIT_POST_MODAL = "EDIT_POST_MODAL";

// Lazy registration to avoid circular dependency with EditPostModal
let modalRegistration: Promise<void> | undefined;
function ensureModalRegistered(): Promise<void> {
  modalRegistration ??= import("../components/CreatePostModal")
    .then(({ EditPostModal }) => {
      registerModal(EDIT_POST_MODAL, EditPostModal);
    })
    .catch((error) => {
      modalRegistration = undefined;
      throw error;
    });
  return modalRegistration;
}

type OpenEditPostModalPayload = {
  callback?(data: SaveFormResult): void;
  onCloseCallback?(post: Post): void;
  post: Post;
  onSubmit?(post: Post | Post[]): Post;
};

export const openEditPostModal = createAction<OpenEditPostModalPayload>(
  "OPEN_EDIT_POST_MODAL"
);
export const closeEditPostModal = createAction("CLOSE_EDIT_POST_MODAL");

function* editPostModalSaga({
  payload,
}: {
  payload: OpenEditPostModalPayload;
  type: string;
}) {
  const { callback, onSubmit, onCloseCallback, ...props } = payload;
  const { post } = props;

  // Ensure modal is registered before opening
  yield call(ensureModalRegistered);

  yield put(
    openModal({
      type: EDIT_POST_MODAL,
      props,
    })
  );

  const formData: SaveFormResult = yield editPost(post, onSubmit);
  if (formData && typeof callback === "function") {
    callback(formData);
  } else if (formData) {
    yield put(reloadPostsAction());
  }

  if (!formData && typeof onCloseCallback === "function") {
    onCloseCallback(post);
  }

  yield put(
    closeModal({
      type: EDIT_POST_MODAL,
    })
  );
}

export function* editModalPostSaga(): Generator<unknown, void, unknown> {
  yield takeEvery(openEditPostModal.type, editPostModalSaga);
}
